import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PlatformIcon, { platformLabel } from "./PlatformIcon";
import { SeverityPill, RiskBadge, timeAgo } from "./bits";
import { ExternalLink, Heart, MessageCircle, Repeat2, MapPin } from "lucide-react";

export default function PostModal({ post, onClose, onJumpToMap }) {
  return (
    <Dialog open={!!post} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl rounded-sm p-0 overflow-hidden" data-testid="post-modal">
        {post && (
          <div>
            <DialogHeader className="px-5 py-4 border-b border-slate-200 bg-slate-50">
              <DialogTitle className="font-display text-sm font-semibold flex items-center gap-2">
                <PlatformIcon platform={post.platform} className="h-4 w-4" />
                {post.author}
                <span className="font-mono text-xs text-slate-400">{post.handle}</span>
                <span className="ml-auto"><SeverityPill severity={post.severity} /></span>
              </DialogTitle>
              <DialogDescription className="sr-only">
                Signal details: {post.activity_category} at {post.site} — severity {post.severity}
              </DialogDescription>
            </DialogHeader>
            <div className="px-5 py-4">
              <div className="bg-slate-50 border-l-4 border-[#2563eb] p-4 mb-4">
                <div className="kicker text-[#2563eb] mb-1">Executive Summary</div>
                <p className="text-[15px] text-slate-900 font-medium leading-relaxed">
                  {post.ai_summary || "Automated analysis in progress. Please check back shortly for the strategic summary."}
                </p>
              </div>
              {post.image && <img src={post.image} alt="" className="mt-4 w-full h-52 object-cover rounded-sm" />}
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                <KV k="Venture" v={post.venture} />
                <KV k="Site" v={post.site} />
                <KV k="Category" v={post.activity_category} />
                <KV k="Subpoint" v={post.activity_subpoint} />
                <KV k="Region" v={`${post.location?.state}`} />
                <KV k="Platform" v={platformLabel(post.platform)} />
                <KV k="Sentiment" v={post.sentiment} />
                <KV k="Posted" v={timeAgo(post.created_at)} />
              </div>
              <div className="mt-4 flex flex-wrap gap-1.5">
                {(post.tags || []).map((t) => (
                  <span key={t} className="font-mono text-[10px] text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-sm">{t}</span>
                ))}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
              <div className="flex items-center gap-4 text-xs text-slate-500 font-mono">
                <span className="flex items-center gap-1"><Heart className="h-3.5 w-3.5" /> {post.likes}</span>
                <span className="flex items-center gap-1"><Repeat2 className="h-3.5 w-3.5" /> {post.shares}</span>
                <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> {post.comments}</span>
              </div>
              <div className="flex items-center gap-3">
                <RiskBadge score={post.risk_score} />
                {onJumpToMap && post.location && (
                  <button
                    onClick={() => { onJumpToMap(post); onClose(); }}
                    className="flex items-center gap-1.5 text-xs text-[#2563eb] hover:underline"
                    data-testid="modal-jump-to-map"
                  >
                    <MapPin className="h-3.5 w-3.5" /> View on map
                  </button>
                )}
                <a href={post.url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 flex items-center gap-1">
                  Open source <ExternalLink className="h-3 w-3" />
                </a>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <div className="kicker">{k}</div>
      <div className="text-[12px] text-slate-800">{String(v)}</div>
    </div>
  );
}
